import { type Pagination } from '@on3/api';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import styles from './Expanded.module.scss';

export const ExpandedPagination = ({
  data,
}: {
  data: Pagination | undefined;
}) => {
  const router = useRouter();
  const { currentPage = 1, pageCount = 1 } = data || {};

  function handleNext() {
    router.push({
      query: { ...router.query, page: currentPage + 1 },
    });
  }

  function handleGoToPage(page: number) {
    router.push({
      query: { ...router.query, page },
    });
  }

  function handlePrevious() {
    router.push({
      query: { ...router.query, page: currentPage - 1 },
    });
  }

  // Show 3 buttons, centered around the current page
  // If there are less than 3 pages, show all pages
  const buttonsToShow = useMemo(() => {
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(pageCount, start + 2);

    const buttons = [];

    for (let i = start; i <= end; i++) {
      buttons.push({ page: i });
    }

    return buttons;
  }, [currentPage, pageCount]);

  return (
    <nav className={styles.block} data-ui="pagination">
      <button
        disabled={currentPage === 1}
        name="previous"
        onClick={handlePrevious}
        type="button"
      >
        <span className="sr-only">Previous</span>
      </button>
      {buttonsToShow.map((button) => (
        <button
          className={clsx({ [styles.active]: button.page === currentPage })}
          key={button.page}
          onClick={() => handleGoToPage(button.page)}
          type="button"
        >
          {button.page}
        </button>
      ))}
      <button
        disabled={currentPage === pageCount}
        name="next"
        onClick={handleNext}
        type="button"
      >
        <span className="sr-only">Next</span>
      </button>
    </nav>
  );
};
